import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

import { restPoster } from "~/api/fetchers";
import { zStringToDate } from "~/utilities/zodInputHelpers";

const CLIENTS_ENDPOINT = "clients";

const clientInvitePayloadSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
});

export type ClientInvitePayload = z.infer<typeof clientInvitePayloadSchema>;

const clientInviteResponseSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  selfUpdatedLast: z.boolean(),
  medicalChecklistId: z.number(),
  clinicId: z.number(),
  status: z.object({
    id: z.number(),
    active: z.boolean(),
    clinicId: z.number().optional(),
    created: zStringToDate(),
  }),
  statuses: z.array(
    z.object({
      id: z.number(),
      active: z.boolean(),
      created: zStringToDate(),
    })
  ),
  created: zStringToDate(),
  patientRelationshipsA: z.array(z.unknown()),
  patientRelationshipsB: z.array(z.unknown()),
  patientTags: z.array(z.unknown()),
  patientsReferrals: z.array(z.unknown()),
  addresses: z.array(z.unknown()),
  teams: z.array(z.unknown()).optional(),
  restricted: z.boolean(),
  accessLevel: z.string(),
  sid: z.string(),
  securePatientId: z.number(),
  groupingKey: z.number(),
});

export type ClientInviteResponse = z.infer<typeof clientInviteResponseSchema>;

export const useInviteClientMutation = () =>
  useMutation({
    mutationFn: async ({ variables }: { variables: ClientInvitePayload }) => {
      clientInvitePayloadSchema.parse(variables);
      const resp = await restPoster<ClientInvitePayload, ClientInviteResponse>(
        `/v1/${CLIENTS_ENDPOINT}`,
        variables
      );
      return clientInviteResponseSchema.parse(resp);
    },
  });

const sendInvitePayloadSchema = z.object({
  inviteSubject: z.string(),
  inviteMessage: z.string(),
  inviteHeading: z.string(),
  inviteButton: z.string(),
});

export type SendInvitePayload = z.infer<typeof sendInvitePayloadSchema>;

type SendInviteMutationVariables = {
  clientId: number;
  variables: SendInvitePayload;
};

export const useSendInviteMutation = () =>
  useMutation({
    mutationFn: async ({ clientId, variables }: SendInviteMutationVariables) => {
      sendInvitePayloadSchema.parse(variables);
      return restPoster(`/v1/client-invites/${clientId}/sendLink`, variables);
    },
  });
